import React, { ComponentType, useCallback, useMemo, useState } from "react";

import type { Dayjs } from "dayjs";
import { DownloadOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import KpiCardJs from "../../shared/Dashboard/KpiCard/KpiCard";
import DatePickerJs from "../../shared/DatePicker/DatePicker";
import Button from "../../shared/Button/Button";

import DailyUsers from "./DailyUsers/DailyUsers";
import AcquisitionByState from "./AcquisitionByState/AcquisitionByState";
import AcquisitionSourceSelect from "./AcquisitionSourceSelect";

import { useUserAnalyticsKpis } from "../../../queries/user-analytics/useUserAnalyticsKpis";
import { useUserAnalyticsDaily } from "../../../queries/user-analytics/useUserAnalyticsDaily";
import { useUserAnalyticsMonthly } from "../../../queries/user-analytics/useUserAnalyticsMonthly";
import { useUserAnalyticsByState } from "../../../queries/user-analytics/useUserAnalyticsByState";
import { useGetFinancialsKpiCards } from "../../../queries/financials/useGetFinancialsKpiCards";
import type { UserAnalyticsKpisResult } from "../../../queries/user-analytics/userAnalyticsKpisTypes";
import type { UserAnalyticsByStateMetric } from "../../../queries/user-analytics/userAnalyticsByStateTypes";
import type { FinancialsKpiCardsResponse } from "../../../queries/financials/useGetFinancialsKpiCards";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { DATE_RANGE, KPI_CARD_BACKGROUND } from "../../../utils/Constants";
import { getAdminNow } from "../../../utils/dateService";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../utils/narrowViewportChartDates";

import {
  buildAcquisitionCsv,
  buildAcquisitionCsvFilename,
  downloadCsv,
} from "./acquisitionCsv";

import styles from "./Acquisition.module.css";
import { useKpiTabToolbar } from "../KpiTabToolbarContext";
import { useClientCountry } from "../../../hooks/useClientCountry";
import type { AcquisitionSource } from "../../../queries/acquisition/acquisitionSourceFilter";

const KpiCard = KpiCardJs as ComponentType<any>;
const CustomDatePicker = DatePickerJs as ComponentType<any>;

const API_DATE_FORMAT = "YYYY-MM-DD";

type DateRange = [Dayjs, Dayjs];

interface KpiItem {
  title: string;
  value: number;
  percentage: number;
  rate?: boolean;
  maximumFractionDigits?: number;
  titleTooltip?: string;
  hidePercentage?: boolean;
}

interface AcquisitionProps {
  currencySymbol?: string;
}

/**
 * KPI ordering — matches the Acquisition section on the Overview V2 page so
 * admins see the same card order on both screens:
 *   Top row    : Total Users (info tooltip — all-time), New Users, FTD Count
 *   Second row : FTD Amount, Avg FTD Amount, Total Active Users (Deposit)
 *
 * `TOTAL USERS` is sourced from a cumulative aggregation that ignores the
 * date filter, so we surface that distinction inline via an "i" tooltip
 * instead of leaving admins to infer it from the unchanging value.
 *
 * `TOTAL ACTIVE USERS (DEPOSIT)` lives on the financials KPI cards
 * response, not user-analytics — pulled in as a second argument so the
 * Acquisition page can surface "how many users are funded depositors"
 * without requiring a BE schema change.
 */
const buildAcquisitionKpis = (
  data: UserAnalyticsKpisResult | undefined,
  financialsData: FinancialsKpiCardsResponse | undefined,
): KpiItem[] => [
  {
    title: "TOTAL USERS",
    value: data?.total_users.value ?? 0,
    percentage: data?.total_users.percent_change ?? 0,
    titleTooltip: AppStrings.TOTAL_USERS_ALL_TIME_TOOLTIP,
    hidePercentage: true,
  },
  {
    title: "NEW USERS",
    value: data?.new_users.value ?? 0,
    percentage: data?.new_users.percent_change ?? 0,
  },
  {
    title: "FTD COUNT",
    value: data?.ftd_count.value ?? 0,
    percentage: data?.ftd_count.percent_change ?? 0,
  },
  {
    title: "FTD AMOUNT",
    value: data?.ftd_amount.value ?? 0,
    percentage: data?.ftd_amount.percent_change ?? 0,
    maximumFractionDigits: 2,
  },
  {
    title: "AVG FTD AMOUNT",
    value: data?.avg_ftd_amount.value ?? 0,
    percentage: data?.avg_ftd_amount.percent_change ?? 0,
    maximumFractionDigits: 2,
  },
  {
    title: AppStrings.OVERVIEW_ACTIVE_DEPOSIT_USERS,
    value: financialsData?.active_deposit_users?.value ?? 0,
    percentage: financialsData?.active_deposit_users?.percent_change ?? 0,
    maximumFractionDigits: 0,
  },
];

const Acquisition: React.FC<AcquisitionProps> = ({ currencySymbol }) => {
  // ── Date state lifted from each section to this parent ─────────────
  // The page-level CSV download needs to know the exact filter active on
  // every section, and each section uses a different default range. We
  // keep independent ranges (KPIs MTD, Daily Last 30, Monthly Last 12,
  // By-State MTD) and pass them down. At 768px and below, daily-grain
  // pickers default to Last 7 days via `narrowViewportChartDates`; Monthly
  // stays Last 12 Months. The hook calls live here for the same reason —
  // the export helper can read the freshest data without each child
  // having to bubble it back up.

  // TCM-driven country code → drives the geo map in `AcquisitionByState`
  // (Mexico for Infinito / Estadio Gana, Nigeria for BetCorrect / Scorama,
  // US for ReBet / DoggHouse). Mirrors the V1 `Sportsbook/Kpi/UsersByState`
  // pattern.
  const clientCountry = useClientCountry();

  const [kpisDates, setKpisDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    }),
  );
  const [kpisStartRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );
  const [dailyDates, setDailyDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.subtract(29, "day").startOf("day"), now.endOf("day")];
    }),
  );
  const [dailyStartRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.LAST_30_DAYS),
  );
  const [monthlyDates, setMonthlyDates] = useState<DateRange>(() => {
    const now = getAdminNow();
    return [now.subtract(11, "month").startOf("month"), now.endOf("day")];
  });
  const [byStateDates, setByStateDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    }),
  );
  const [byStateStartRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );
  const [byStateMetric, setByStateMetric] =
    useState<UserAnalyticsByStateMetric>("new_users");
  const [kpisSources, setKpisSources] = useState<AcquisitionSource[]>([]);
  const [kpisSourceDraft, setKpisSourceDraft] = useState<AcquisitionSource[]>(
    [],
  );
  const [dailySources, setDailySources] = useState<AcquisitionSource[]>([]);
  const [monthlySources, setMonthlySources] = useState<AcquisitionSource[]>([]);
  const [byStateSources, setByStateSources] = useState<AcquisitionSource[]>([]);
  const [kpisSourceFilterVisible, setKpisSourceFilterVisible] = useState(false);

  const kpisStart = kpisDates[0]?.format(API_DATE_FORMAT) ?? "";
  const kpisEnd = kpisDates[1]?.format(API_DATE_FORMAT) ?? "";
  const dailyStart = dailyDates[0]?.format(API_DATE_FORMAT) ?? "";
  const dailyEnd = dailyDates[1]?.format(API_DATE_FORMAT) ?? "";
  const monthlyStart = monthlyDates[0]?.format(API_DATE_FORMAT) ?? "";
  const monthlyEnd = monthlyDates[1]?.format(API_DATE_FORMAT) ?? "";
  const byStateStart = byStateDates[0]?.format(API_DATE_FORMAT) ?? "";
  const byStateEnd = byStateDates[1]?.format(API_DATE_FORMAT) ?? "";

  const {
    data: kpisData,
    isFetching: isFetchingKpis,
    isError: isKpisError,
  } = useUserAnalyticsKpis({
    start_date: kpisStart,
    end_date: kpisEnd,
    source: kpisSources,
  });
  // `active_deposit_users` lives on the financials response. We hit
  // /financials/kpi-cards from this page so the on-screen 6th card has
  // its own data without requiring a BE schema change. React Query
  // dedupes the request when the Overview page is also active, so this
  // is free in the common case. `isFetching` is OR'd into the grid's
  // skeleton flag below — without it the 6th card briefly shows 0
  // while the other 5 are already populated (cold-cache flicker).
  const {
    data: financialsKpiData,
    isFetching: isFetchingFinancials,
  } = useGetFinancialsKpiCards({
    start_date: kpisStart,
    end_date: kpisEnd,
    source: kpisSources,
  });
  const {
    data: dailyData,
    isFetching: isFetchingDaily,
    isError: isDailyError,
  } = useUserAnalyticsDaily({
    start_date: dailyStart,
    end_date: dailyEnd,
    source: dailySources,
  });
  const {
    data: monthlyData,
    isFetching: isFetchingMonthly,
    isError: isMonthlyError,
  } = useUserAnalyticsMonthly({
    start_date: monthlyStart,
    end_date: monthlyEnd,
    source: monthlySources,
  });
  const {
    data: byStateData,
    isFetching: isFetchingByState,
    isError: isByStateError,
  } = useUserAnalyticsByState({
    start_date: byStateStart,
    end_date: byStateEnd,
    source: byStateSources,
  });

  const acquisitionKpis = useMemo(
    () => buildAcquisitionKpis(kpisData, financialsKpiData),
    [kpisData, financialsKpiData],
  );
  const hasKpisSourceChanges = useMemo(
    () =>
      kpisSources.length !== kpisSourceDraft.length ||
      kpisSources.some((source) => !kpisSourceDraft.includes(source)),
    [kpisSourceDraft, kpisSources],
  );
  const monthlyPresetOptions = useMemo(() => {
    const now = getAdminNow();
    return [
      {
        label: AppStrings.LAST_12_MONTHS,
        value: [
          now.subtract(11, "month").startOf("month"),
          now.endOf("day"),
        ] as DateRange,
      },
    ];
  }, []);

  // The DatePicker still calls back through `getData` for legacy parity, but the
  // actual fetch is driven by `dates` -> hook -> React Query. Keep this as a
  // no-op so the picker stays a controlled component.
  const getData = useCallback((_queryString: string | null) => undefined, []);
  const handleMonthlyDatesChange = useCallback((next: DateRange) => {
    const today = getAdminNow();
    setMonthlyDates([
      next[0].startOf("month"),
      next[1].isSame(today, "month")
        ? today.endOf("day")
        : next[1].endOf("month"),
    ]);
  }, []);

  // Page toolbar download is scoped to the KPI cards only — the Daily
  // Users chart and Acquisition By State map have their own per-section
  // download controls. Mirroring the `Financials` / `Sportsbook` /
  // `Casino` subtabs where the page-level button only exports the cards
  // visible at the top of the page.
  const handleDownloadCsv = useCallback(() => {
    const csv = buildAcquisitionCsv({
      kpisDates,
      kpis: kpisData,
      financials: financialsKpiData,
    });
    downloadCsv(buildAcquisitionCsvFilename(), csv);
  }, [kpisDates, kpisData, financialsKpiData]);

  // Portal the date picker + download button into the shared KPI tabs
  // row so they sit next to the tab strip and wrap below it on narrow
  // viewports (e.g. iPhone landscape) instead of overlapping the tabs.
  const tabToolbar = useMemo(
    () => (
      <>
        <Button
          onClick={() =>
            setKpisSourceFilterVisible((visible) => {
              if (!visible) setKpisSourceDraft(kpisSources);
              return !visible;
            })
          }
          aria-expanded={kpisSourceFilterVisible}
          aria-controls="acquisition-kpi-source-filter"
          aria-label={AppStrings.PAM_REPORT_FILTERS}
        >
          <Filter size={18} aria-hidden="true" />
          {kpisSourceFilterVisible ? (
            <ChevronUp size={16} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} aria-hidden="true" />
          )}
        </Button>
        <CustomDatePicker
          getData={getData}
          dates={kpisDates}
          setDates={setKpisDates}
          showPresets={true}
          startRange={kpisStartRange}
        />
        <button
          type="button"
          className={styles.downloadButton}
          onClick={handleDownloadCsv}
          aria-label={AppStrings.ACQUISITION_DOWNLOAD_FULL_PAGE_ARIA}
          data-testid="acquisition-download-full-page"
        >
          <DownloadOutlined />
        </button>
      </>
    ),
    [
      getData,
      kpisDates,
      handleDownloadCsv,
      kpisSourceFilterVisible,
      kpisSources,
      kpisStartRange,
    ],
  );

  useKpiTabToolbar(tabToolbar);

  return (
    <section
      className={styles.acquisition}
      aria-label="Acquisition KPIs"
      data-testid="acquisition"
    >
      {isKpisError ? (
        <p
          className={styles.errorBanner}
          role="alert"
          data-testid="acquisition-kpi-error"
        >
          {AppStrings.ACQUISITION_KPIS_LOAD_ERROR}
        </p>
      ) : null}

      {kpisSourceFilterVisible && (
        <div
          id="acquisition-kpi-source-filter"
          className={styles.kpiSourceFilter}
          data-testid="acquisition-kpi-source-filter"
        >
          <div className={styles.filterField}>
            <span className={styles.filterLabel}>
              {AppStrings.SOURCE}
            </span>
            <AcquisitionSourceSelect
              value={kpisSourceDraft}
              onChange={setKpisSourceDraft}
              loading={isFetchingKpis || isFetchingFinancials}
              className={styles.sourceSelect}
              testId="acquisition-kpi-source-select"
            />
          </div>
          <div className={styles.filterActions}>
            <Button
              intent="neutral"
              variant="outline"
              size="large"
              onClick={() => setKpisSourceDraft([])}
            >
              {AppStrings.CLEAR_FILTERS}
            </Button>
            <Button
              intent="primary"
              size="large"
              onClick={() => setKpisSources(kpisSourceDraft)}
              disabled={!hasKpisSourceChanges}
            >
              {AppStrings.APPLY}
            </Button>
          </div>
        </div>
      )}

      <div className={styles.kpiGrid} data-testid="acquisition-kpi-grid">
        {acquisitionKpis.map((item) => (
          <KpiCard
            key={item.title}
            // OR both fetch flags so the 6th card (Total Active Users
            // (Deposit), sourced from /financials) doesn't render its 0
            // placeholder while the other 5 are already populated.
            isApiRunning={isFetchingKpis || isFetchingFinancials}
            title={item.title}
            titleTooltip={item.titleTooltip}
            value={item.value}
            percentage={item.percentage}
            hidePercentage={item.hidePercentage ?? false}
            rate={item.rate ?? false}
            maximumFractionDigits={item.maximumFractionDigits ?? 0}
            currencySymbol={currencySymbol}
            hugeValue={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        ))}
      </div>

      <DailyUsers
        dates={dailyDates}
        onDatesChange={setDailyDates}
        data={dailyData}
        isFetching={isFetchingDaily}
        isError={isDailyError}
        sources={dailySources}
        onSourcesChange={setDailySources}
        startRange={dailyStartRange}
      />

      <AcquisitionByState
        dates={byStateDates}
        onDatesChange={setByStateDates}
        country={clientCountry}
        data={byStateData}
        isFetching={isFetchingByState}
        isError={isByStateError}
        selectedMetric={byStateMetric}
        onMetricChange={setByStateMetric}
        sources={byStateSources}
        onSourcesChange={setByStateSources}
        startRange={byStateStartRange}
      />

      <DailyUsers
        dates={monthlyDates}
        onDatesChange={handleMonthlyDatesChange}
        data={monthlyData}
        isFetching={isFetchingMonthly}
        isError={isMonthlyError}
        sources={monthlySources}
        onSourcesChange={setMonthlySources}
        startRange={AppStrings.LAST_12_MONTHS}
        presetOptions={monthlyPresetOptions}
        axisDateFormat="MMM YYYY"
        tooltipDateFormat="MMMM YYYY"
        exportFilename="acquisition-monthly-users.xlsx"
        includeFtdAmount={false}
        picker="month"
        sourceTestId="acquisition-monthly-source-select"
        testId="monthly-users"
        title={AppStrings.PAM_ACQUISITION_MONTHLY_USERS_TITLE}
        titleTooltip={AppStrings.PAM_ACQUISITION_MONTHLY_USERS_TOOLTIP}
        ariaLabel={AppStrings.PAM_ACQUISITION_MONTHLY_USERS_ARIA}
        errorMessage={AppStrings.PAM_ACQUISITION_MONTHLY_LOAD_ERROR}
      />
    </section>
  );
};

export default Acquisition;
